/** Modèle et logique de filtrage des convives d'un événement — extraits de ConvivesImportSection pour
 *  rester sous la limite de 500 lignes par fichier et pour que le filtrage soit lisible/testable seul.
 *  Aucun rendu ici : uniquement des fonctions pures. */

export interface Guest {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    invitation_sent_at: string | null;
    responded_at: string | null;
    email_count?: number;
    // Colonnes JSON renvoyées brutes (chaîne) par le backend — jamais parsées côté serveur, comme le reste
    // des colonnes JSON de ce module. Le type accepte les deux formes pour rester tolérant.
    restrictions?: string[] | string | null;
    allergies?: string[] | string | null;
    menu_skipped_categories?: string[] | string | null;
    menu_alt_requested?: string[] | string | null;
    medical_note?: string | null;
    table_number?: string | null;
    /** Noms des plats choisis, par catégorie (« Plat » → « Filet de bœuf ») — résolus par le serveur. */
    dish_choices?: Record<string, string> | null;
}

/** Filtre "réponse" — valeurs alignées sur StatusFilterChips, qui utilise 'all' pour sa puce "Tous". */
export type ResponseFilter = 'all' | 'non_invite' | 'en_attente' | 'repondu';

export interface ConvivesFilters {
    search: string;
    response: ResponseFilter;
    withAllergies: boolean;
    withRestrictions: boolean;
    withMenuAlert: boolean;
}

export const EMPTY_FILTERS: ConvivesFilters = {
    search: '',
    response: 'all',
    withAllergies: false,
    withRestrictions: false,
    withMenuAlert: false,
};

export const hasActiveFilters = (f: ConvivesFilters): boolean =>
    f.search.trim() !== '' || f.response !== 'all' || f.withAllergies || f.withRestrictions || f.withMenuAlert;

const parseList = (value: string[] | string | null | undefined): string[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value || '[]');
            return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
        } catch {
            return [];
        }
    }
    return [];
};

export const guestAllergies = (g: Guest): string[] => parseList(g.allergies);
export const guestRestrictions = (g: Guest): string[] => parseList(g.restrictions);
/** Catégories que le convive a explicitement déclinées ("aucun plat ne me convenait") — c'est ce que
 *  remontait la colonne ALERTE du tableau, et ce sur quoi s'appuie le filtre "n'a pas choisi de plat". */
export const guestSkippedCategories = (g: Guest): string[] => parseList(g.menu_skipped_categories);
/** Catégories pour lesquelles le convive ATTEND une réponse de l'établissement — à distinguer de celles
 *  qu'il a simplement déclinées : ici il veut manger, et personne d'autre que l'établissement ne peut le
 *  débloquer. Demande d'Emeni (2026-08-24). */
export const guestAltRequested = (g: Guest): string[] => parseList(g.menu_alt_requested);
export const guestMedicalNote = (g: Guest): string => (g.medical_note || '').trim();
/** Le plat à afficher dans la colonne « Choix de plat » : le plat principal s'il existe, sinon le premier
 *  choix — et une chaîne vide tant que le convive n'a rien choisi. */
export const guestMainDish = (g: Guest): string => {
    const c = g.dish_choices && typeof g.dish_choices === 'object' ? g.dish_choices : {};
    const principal = Object.entries(c).find(([cat]) => /^plat/i.test(cat));
    return (principal ? principal[1] : Object.values(c)[0]) || '';
};

export const guestResponseState = (g: Guest): Exclude<ResponseFilter, 'all'> => {
    if (g.responded_at) return 'repondu';
    if (g.invitation_sent_at) return 'en_attente';
    return 'non_invite';
};

/** Dimension de filtre à ignorer lors d'un comptage. Sert à afficher sur chaque puce le nombre de convives
 *  qu'elle donnerait *compte tenu des autres filtres actifs* : un compteur calculé sur la liste entière
 *  serait faux (voire trompeur) dès qu'un second filtre est en place. */
export type OmittedFilter = 'response' | 'allergies' | 'restrictions' | 'menuAlert';

export const matchesFilters = (g: Guest, f: ConvivesFilters, omit?: OmittedFilter): boolean => {
    const q = f.search.trim().toLowerCase();
    if (q && !`${g.first_name} ${g.last_name} ${g.email}`.toLowerCase().includes(q)) return false;
    if (omit !== 'response' && f.response !== 'all' && guestResponseState(g) !== f.response) return false;
    if (omit !== 'allergies' && f.withAllergies && guestAllergies(g).length === 0) return false;
    if (omit !== 'restrictions' && f.withRestrictions && guestRestrictions(g).length === 0) return false;
    // L'alerte menu couvre les deux cas : le convive qui a renoncé et celui qui attend une alternative.
    // Les séparer en deux puces aurait multiplié les filtres pour une même question — "qui n'a pas de plat ?".
    if (omit !== 'menuAlert' && f.withMenuAlert && guestSkippedCategories(g).length === 0 && guestAltRequested(g).length === 0) return false;
    return true;
};

export const filterGuests = (guests: Guest[], f: ConvivesFilters, omit?: OmittedFilter): Guest[] =>
    guests.filter((g) => matchesFilters(g, f, omit));
